/**
 * One query key and one query string per endpoint (#2193 B4).
 *
 * A key is the endpoint's path segments after `/api/v1/`, the organisation and
 * the normalised params: `['cockpit', 'occupancy', 'care-ratio', orgId, {...}]`.
 * Requests with the same meaning normalise to the same params, so wherever in
 * the app they start they share one cache entry and one HTTP call. The leading
 * segments keep prefix invalidation working (`['cockpit']`,
 * `['shifts', 'coverage']`), and the organisation keeps one tenant's cached
 * figures away from another after an org switch.
 */

export type EndpointParamValue = string | number | boolean | null | undefined;
export type EndpointParams = Readonly<Record<string, EndpointParamValue>>;
export type NormalisedParams = Readonly<Record<string, string>>;

const API_PREFIX = "/api/v1/";

/** Comma-separated id lists: the server splits them and reads them as sets. */
const ID_LIST_PARAMS = new Set(["childcare_id", "group_id"]);

/**
 * The cockpit metrics that bind `CockpitQueryParams`
 * (`internal/handlers/cockpit/cockpit_dto.go`): the server always compares
 * with the previous year and always answers per month, whatever `compare_to`
 * and `granularity` say. Their canonical form sends `compare_to=previous_year`
 * and leaves `granularity=month` out. `portfolio-documentation/scopes` takes
 * neither and is not listed.
 */
const MONTHLY_COMPARED_METRICS = [
  "children/absences",
  "children/activities",
  "children/arrival-pickup",
  "children/courses",
  "children/enrollment-movement",
  "children/portfolio-documentation",
  "employees/absences",
  "employees/app-usage",
  "financial/contracts",
  "financial/liquidity",
  "financial/payment-methods",
  "financial/payments",
  "financial/revenue",
  "financial/subsidies",
  "occupancy/care-ratio",
  "occupancy/utilization",
  "occupancy/violations",
  "workflows/items",
  "workflows/processing-time",
].map((m) => `${API_PREFIX}cockpit/${m}`);

interface ParamRules {
  /** Always sent: the server assumes these when they are missing. */
  sent?: Readonly<Record<string, string>>;
  /** Left out when they carry this value: the server ignores them. */
  dropped?: Readonly<Record<string, string>>;
}

const PARAM_RULES: ReadonlyMap<string, ParamRules> = new Map<string, ParamRules>([
  ...MONTHLY_COMPARED_METRICS.map(
    (path) => [path, { sent: { compare_to: "previous_year" }, dropped: { granularity: "month" } }] as const,
  ),
  // `none` is a different question and stays explicit.
  [`${API_PREFIX}cockpit/employees/absence-rates`, { sent: { compare_to: "previous_year" } }],
]);

function sortedList(value: string): string {
  return value
    .split(",")
    .filter(Boolean)
    .sort()
    .join(",");
}

/**
 * The params as the server reads them: empty values dropped, values as strings,
 * id lists sorted, the per-endpoint rules applied, keys sorted.
 */
export function normaliseParams(path: string, params: EndpointParams = {}): NormalisedParams {
  const rules = PARAM_RULES.get(path);
  const merged: Record<string, string> = { ...rules?.sent };
  for (const [key, raw] of Object.entries(params)) {
    if (raw === undefined || raw === null || raw === "") continue;
    const value = String(raw);
    merged[key] = ID_LIST_PARAMS.has(key) ? sortedList(value) : value;
  }
  const out: Record<string, string> = {};
  for (const key of Object.keys(merged).sort()) {
    if (merged[key] === "" || rules?.dropped?.[key] === merged[key]) continue;
    out[key] = merged[key];
  }
  return out;
}

/** The path's segments after `/api/v1/`. */
function segments(path: string): string[] {
  const rest = path.startsWith(API_PREFIX) ? path.slice(API_PREFIX.length) : path;
  return rest.split("/").filter(Boolean);
}

/** The query key of a GET on `path` with `params` for an organisation. */
export function endpointKey(orgId: string | null | undefined, path: string, params: EndpointParams = {}) {
  return [...segments(path), orgId ?? null, normaliseParams(path, params)] as const;
}

/** The query string of the same request, without `?`; empty when there are no params. */
export function endpointQueryString(path: string, params: EndpointParams = {}): string {
  return new URLSearchParams(normaliseParams(path, params)).toString();
}

/**
 * `placeholderData` that keeps the previous answer on screen while a new one
 * loads (the chart blurs in place on a filter change), but only within the
 * same organisation: after an org switch the previous org's figures are never
 * shown, not even for a frame (coding standard, "placeholderData must be scoped
 * to a compatible key"). For keys built by `endpointKey`.
 */
export function keepPreviousWithinOrg(orgId: string | null | undefined) {
  return <T>(previous: T | undefined, previousQuery?: { queryKey: readonly unknown[] }): T | undefined =>
    previousQuery && previousQuery.queryKey.at(-2) === (orgId ?? null) ? previous : undefined;
}
